import { RequestManager } from '@jaseeey/request-manager';
import { defineStore } from 'pinia';
import pluralize from 'pluralize';
import {
    sortAllProfileLinks,
    sortAllProfileSections,
    sortAllProfileSectionTags,
    sortProfileSections,
    sortProfileSectionTags
} from 'src/services/user-profile-service';
import { prepareAsFormData } from 'src/utils/form-util';
import { notifyError } from 'src/utils/quasar-util';
import { titleToCamelCase } from 'src/utils/string-util';
import { computed, ref } from 'vue';

const profileTTLMins = 5;

export const useUserProfileStore = defineStore('user-profile', () => {

    const profile = ref(null);
    const profileDirty = ref(false);
    const profileLoadedAt = ref(null);

    const profileLinks = ref([]);
    const profileLinksDirty = ref(false);
    const profileLinksLoadedAt = ref(null);

    const contributions = ref(null);
    const contributionsDirty = ref(false);
    const contributionsLoadedAt = ref(null);

    const profileInvalidated = computed(() => {
        return profileDirty.value
            || !profileLoadedAt.value
            || ((Date.now() - profileLoadedAt) / 1000) / 60 >= profileTTLMins;
    });

    const profileLinksInvalidated = computed(() => {
        return profileLinksDirty.value
            || !profileLinksLoadedAt.value
            || ((Date.now() - profileLinksLoadedAt) / 1000) / 60 >= profileTTLMins;
    });

    const contributionsInvalidated = computed(() => {
        return contributionsDirty.value
            || !contributionsLoadedAt.value
            || ((Date.now() - contributionsLoadedAt) / 1000) / 60 >= profileTTLMins;
    });

    function findSectionByKeyAndTypeId(sectionIdentifier, typeId) {
        const sectionKey = titleToCamelCase(pluralize(typeId));
        const sectionIndex = profile.value[sectionKey].findIndex(s => s.key === sectionIdentifier);
        return profile.value[sectionKey][sectionIndex];
    }

    function invalidateProfile() {
        profileDirty.value = true;
    }

    function invalidateProfileLinks() {
        profileLinksDirty.value = true;
    }

    function invalidateContributions() {
        contributionsDirty.value = true;
    }

    async function loadProfile() {
        if (!profileInvalidated.value) {
            return;
        }
        const response = await RequestManager.call(this.$api, 'get', '/v1/user/profile');
        profile.value = response.data;
        sortAllProfileSections(profile.value);
        sortAllProfileSectionTags(profile.value);
        profileDirty.value = false;
        profileLoadedAt.value = Date.now();
    }

    async function loadProfileLinks() {
        if (!profileLinksInvalidated.value) {
            return;
        }
        try {
            const response = await RequestManager.call(this.$api, 'get', '/v1/user/profile/links');
            profileLinks.value = response.data;
            sortAllProfileLinks(profileLinks.value);
            profileLinksDirty.value = false;
            profileLinksLoadedAt.value = Date.now();
        } catch {
            notifyError('Links could not be loaded, please refresh and try again.');
        }
    }

    async function loadContributions() {
        if (!contributionsInvalidated.value) {
            return;
        }
        try {
            const response = await RequestManager.call(this.$api, 'get', '/v1/user/profile/contributions');
            contributions.value = response.data;
            contributionsDirty.value = false;
            contributionsLoadedAt.value = Date.now();
        } catch {
            notifyError('Contributions could not be loaded, please refresh and try again.');
        }
    }

    async function saveProfile(updatedProfile) {
        updatedProfile.accessMode ??= null;
        updatedProfile.attributes ??= null;
        // A profile the reader already has is updated; one they do not is created. Read from the loaded profile
        // rather than from an identifier on the submitted object, which no longer carries one.
        profile.value = profile.value
            ? (await this.$api.put('/v1/user/profile', updatedProfile)).data
            : (await this.$api.post('/v1/user/profile', updatedProfile)).data;
        sortAllProfileSections(profile.value);
        sortAllProfileSectionTags(profile.value);
        profileDirty.value = false;
        profileLoadedAt.value = Date.now();
    }

    async function saveAlias(alias) {
        const resource = (await this.$api.put('/v1/user/profile/alias', { alias })).data;
        profile.value.alias = resource.alias;
    }

    async function saveProfileLink(userProfileLink) {
        const linkIndex = userProfileLink.key
            ? profileLinks.value.findIndex(l => l.key === userProfileLink.key)
            : null;
        const resource = userProfileLink.key
            ? (await this.$api.put(`/v1/user/profile/links/${userProfileLink.key}`, userProfileLink)).data
            : (await this.$api.post('/v1/user/profile/links', userProfileLink)).data;
        if (linkIndex === null) {
            (profileLinks.value = profileLinks.value ?? []).unshift(resource);
            return;
        }
        profileLinks.value.splice(linkIndex, 1, resource);
    }

    async function shareProfileLink(userProfileLink, attributes, captcha = {}) {
        await this.$api.postWithCaptcha(`/v1/user/profile/links/${userProfileLink.key}/send`, {
            recipient: {
                name: attributes.recipient.name,
                email: attributes.recipient.email
            },
            subject: attributes.subject,
            message: attributes.message
        }, { captcha });
    }

    async function deleteProfileLink(userProfileLinkKey) {
        const userProfileLink = profileLinks.value.find(l => l.key === userProfileLinkKey);
        await this.$api.delete(`/v1/user/profile/links/${userProfileLinkKey}`);
        userProfileLink.deletedAt = new Date();
        userProfileLink.status = 'INACTIVE';
        sortAllProfileLinks(profileLinks.value);
    }

    async function saveProfilePhoto(blob) {
        const formData = prepareAsFormData({ file: blob });
        const resource = (await this.$api.put('/v1/user/profile/photo', formData)).data;
        profile.value.photoUrl = resource.url;
    }

    async function deleteProfilePhoto() {
        await this.$api.delete('/v1/user/profile/photo');
        profile.value.photoUrl = null;
    }

    async function saveSection(section) {
        const sectionKey = titleToCamelCase(pluralize(section.typeId));
        const sectionIndex = section.key
            ? profile.value?.[sectionKey].findIndex(s => s.key === section.key)
            : null;
        const resource = section.key
            ? (await this.$api.put(`/v1/user/profile/sections/${section.key}`, section)).data
            : (await this.$api.post('/v1/user/profile/sections', section)).data;
        sortProfileSectionTags(resource);
        if (sectionIndex === null) {
            (profile.value[sectionKey] = profile.value[sectionKey] ?? []).push(resource);
        } else {
            profile.value[sectionKey].splice(sectionIndex, 1, resource);
        }
        _updatePrimaryWorkplaceKey(resource);
        sortProfileSections(sectionKey, profile.value[sectionKey]);
    }

    /**
     * Updates the profile's primaryWorkplaceKey if the endedAt date was modified. This replicates the logic within the
     * server to prevent having to trigger a full reload of the user's profile.
     *
     * @param {Object} section
     * @private
     */
    function _updatePrimaryWorkplaceKey(section) {
        if (section.typeId !== 'Workplace') {
            return;
        }
        if (profile.value.primaryWorkplaceKey === null && section.endedAt === null) {
            profile.value.primaryWorkplaceKey = section.key;
            return;
        }
        if (section.key === profile.value.primaryWorkplaceKey && section.endedAt) {
            const otherWorkplace = profile.value.workplaces
                .filter(w => w.endedAt === null)
                .sort(_compareWorkplacesNewestFirst)?.[0];
            profile.value.primaryWorkplaceKey = otherWorkplace ? otherWorkplace.key : null;
        }
    }

    // Newest first, as the server picks it: by creation time, then by key for two created in the same instant. Keys
    // share one length and a lowercase alphanumeric alphabet, so comparing them as strings matches the database order.
    function _compareWorkplacesNewestFirst(a, b) {
        const byCreation = new Date(b.createdAt) - new Date(a.createdAt);
        if (byCreation !== 0 || a.key === b.key) {
            return byCreation;
        }
        return a.key < b.key ? 1 : -1;
    }

    async function deleteSection(section) {
        const sectionKey = titleToCamelCase(pluralize(section.typeId));
        const sectionIndex = profile.value[sectionKey].findIndex(s => s.key === section.key);
        await this.$api.delete(`/v1/user/profile/sections/${section.key}`);
        profile.value[sectionKey].splice(sectionIndex, 1);
        _nullifyPrimaryWorkplaceKey(section);
    }

    /**
     * Nullifies the profile's primaryWorkplaceKey if the deleted section was the designated primary workplace. This
     * replicates the logic within the server to prevent having to trigger a full reload of the user's profile.
     *
     * @param {Object} section
     * @private
     */
    function _nullifyPrimaryWorkplaceKey(section) {
        // Matched on the key, as the sibling above it is. Sections stopped carrying an id, so comparing one left this
        // guard true for every section and the cached profile still naming a workplace that had just been removed.
        if (section.typeId !== 'Workplace' || section.key !== profile.value.primaryWorkplaceKey) {
            return;
        }
        profile.value.primaryWorkplaceKey = null;
    }

    async function bulkCreateSectionFiles(section, files) {
        const loadedSection = findSectionByKeyAndTypeId.bind(this)(section.key, section.typeId);
        const payload = prepareAsFormData({ files });
        const response = await this.$api.post(`/v1/user/profile/sections/${section.key}/files/bulk`, payload);
        const successfulUploads = response.data.filter(result => result.status === 'success');
        loadedSection.files.push(...successfulUploads.map(f => f.data));
    }

    async function bulkDeleteSectionFiles(section, files) {
        const loadedSection = findSectionByKeyAndTypeId.bind(this)(section.key, section.typeId);
        const payload = files.map(({ key }) => ({ key }));
        const response = await this.$api.delete(`/v1/user/profile/sections/${section.key}/files/bulk`, { data: payload });
        const successfulDeletes = response.data.filter(result => result.status === 'success');
        successfulDeletes.forEach(delFile => {
            const fileIndex = loadedSection.files.findIndex(f => f.key === delFile.key);
            if (fileIndex !== -1) loadedSection.files.splice(fileIndex, 1);
        });
    }

    async function saveAccessMode(accessMode) {
        const response = await this.$api.patch('/v1/user/profile', { accessMode });
        if (profile.value) {
            profile.value.accessMode = response.data.accessMode;
        }
        return response.data.accessMode;
    }

    async function requestReview() {
        if (!profile.value) {
            throw new Error('Profile not loaded');
        }
        const res = await this.$api.post('/v1/user/profile/request-review');
        profile.value.status = 'UNDER_REVIEW';
        return res.data.id;
    }

    return {
        profile,
        profileDirty,
        profileLoadedAt,
        profileLinks,
        profileLinksDirty,
        profileLinksLoadedAt,
        contributions,
        contributionsDirty,
        contributionsLoadedAt,
        profileInvalidated,
        profileLinksInvalidated,
        contributionsInvalidated,
        invalidateProfile,
        invalidateProfileLinks,
        invalidateContributions,
        loadProfile,
        loadProfileLinks,
        loadContributions,
        saveProfile,
        saveProfilePhoto,
        deleteProfilePhoto,
        saveAlias,
        saveProfileLink,
        shareProfileLink,
        deleteProfileLink,
        saveSection,
        deleteSection,
        bulkCreateSectionFiles,
        bulkDeleteSectionFiles,
        saveAccessMode,
        requestReview
    };
});
